<template>
  <FreeBottomSheet :name="name" persistent @onTryClose="resetToInitialState">
    <template #header>
      <FreeHeader class="p-4 text-center" is-centered>
        <template #default>
          <h1 class="font-medium">
            {{ t('ancillaries.flightDelay.title') }}
          </h1>
        </template>
        <template #rightAction>
          <FreeCloseButton
            class="gray-header-close-button"
            @click="resetToInitialState"
          />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <FlightDelayForm
        :flights="flights"
        @onUpdateInitialState="
          initialSegmentWithPassengers = cloneDeep(
            flightDelayStore.state.segments,
          )
        "
      />
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { onMounted, onUnmounted, ref } from 'vue';
  import {
    FreeBottomSheet,
    FreeCloseButton,
    FreeHeader,
    type flightEntity,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import { cloneDeep } from 'lodash-es';

  import {
    bookingStateModel,
    type bookingStateTypes,
  } from '@/features/booking-state';

  import FlightDelayForm from './FlightDelayForm.vue';
  import { useFlightDelayStore } from '../model';
  import type { SegmentWithPassengers, FlightDelayProduct } from '../types';

  const props = defineProps<{
    name: string;
    flights: flightEntity.types.MappedFlight[];
    passengers: bookingStateTypes.NormalizedPassenger[];
    offerId: string;
    flightDelayProducts: FlightDelayProduct[];
  }>();
  const { t } = useI18n();

  const bottomSheet = controllerModel.useBottomSheet();
  const bookingStore = bookingStateModel.useBookingStore();
  const flightDelayStore = useFlightDelayStore();

  const initialSegmentWithPassengers = ref<SegmentWithPassengers[]>([]);

  onMounted(() => {
    const savedSegments = bookingStore.flightDelayProducts?.[0]?.savedSegments;

    let segmentsToUse;
    if (savedSegments && savedSegments.length > 0) {
      segmentsToUse = cloneDeep(savedSegments);
    } else {
      let globalSegmentIdx = 0;
      segmentsToUse = props.flights.flatMap(flight =>
        flight.segments.map(segment => ({
          ...segment,
          idx: globalSegmentIdx++,
          passengers: props.passengers.map(passenger => ({
            ...passenger,
            isSelected: !!passenger?.iin,
          })),
        })),
      );
    }

    flightDelayStore.init(
      props.flightDelayProducts,
      props.offerId,
      segmentsToUse,
      0,
      0,
      false,
    );

    initialSegmentWithPassengers.value = cloneDeep(segmentsToUse);
  });

  const resetToInitialState = () => {
    flightDelayStore.updateSegments(
      cloneDeep(initialSegmentWithPassengers.value),
    );
    bottomSheet.hide(props.name);
  };

  onUnmounted(() => {
    if (!bookingStore.flightDelayProducts?.length) {
      bookingStore.nullifyFlightDelayProducts();
    }
  });
</script>
